import type {ReactNode} from 'react';
import moment from 'moment';
import type {ContactNote} from 'api/v2';
import {
  DATE_FORMAT_LONG,
  DATE_TIME_FORMAT_LONG,
  DATE_TIME_FORMAT_WEEKDAY,
  TIME_FORMAT_12,
} from '../../constants';
import {formatDate} from '../NoteForm';
import type {SummaryFact, SummaryField, SummaryListItem} from '../NoteSummary';
import {NoteSummary} from '../NoteSummary';
import type {Choice} from './choices';
import {
  CHANGE_STATUSES,
  CONTACT_METHODS,
  FOLLOW_UPS,
  NOT_REACHED,
  NOT_REACHED_REASONS,
  OTHER,
  REASONS,
  RESPONSES,
  SERVICE_ACTIVITIES,
  SERVICE_CODES,
  STATUS_NEEDS,
  labelOf,
} from './choices';
import {billableUnits, unitsLabel} from './rules';

type SectionTitle =
  | 'Narrative'
  | 'Contact'
  | 'Service provided'
  | 'Reason'
  | 'Member status'
  | 'Member response'
  | 'Follow-up'
  | 'Billing';

// Rendering doesn't read this; it makes each new API field get placed, and tests check the placement.
export const FIELD_PLACEMENT = {
  id: 'skip',
  member: 'header',
  dateOfService: 'header',
  staff: 'footer',
  created: 'footer',
  modified: 'footer',
  definitionVersion: 'footer',
  narrative: 'Narrative',
  contactMethod: 'Contact',
  contactMethodDetail: 'Contact',
  notReachedReason: 'Contact',
  notReachedReasonOther: 'Contact',
  nextAttemptAt: 'Contact',
  notReachedNote: 'Contact',
  placeOfServiceCode: 'Service provided',
  placeOfServiceLabel: 'Service provided',
  placeOfServiceOther: 'Service provided',
  activities: 'Service provided',
  specificNeed: 'Service provided',
  serviceDescription: 'Service provided',
  reasons: 'Reason',
  objectiveReason: 'Reason',
  changeStatus: 'Member status',
  needs: 'Member status',
  relevantChange: 'Member status',
  observation: 'Member status',
  responses: 'Member response',
  responseOutcome: 'Member response',
  followUps: 'Follow-up',
  followUpPlan: 'Follow-up',
  followUpAt: 'Follow-up',
  serviceCode: 'Billing',
  startTime: 'Billing',
  endTime: 'Billing',
} as const satisfies Record<
  keyof ContactNote,
  SectionTitle | 'header' | 'footer' | 'skip'
>;

type Checklist = {selected: readonly string[]; other: string};

const tickedItems = (
  {selected, other}: Checklist,
  choices: Choice[],
): SummaryListItem[] => {
  const formOrder = (key: string) => {
    const index = choices.findIndex((choice) => choice.key === key);
    return index === -1 ? choices.length : index;
  };
  return [...selected]
    .sort((a, b) => formOrder(a) - formOrder(b))
    .map((key) => ({
      label: labelOf(choices, key),
      detail: key === OTHER ? other.trim() : undefined,
    }));
};

const durationOf = ({startTime, endTime}: ContactNote) => {
  if (!startTime || !endTime) {
    return null;
  }
  const minutes = moment(endTime).diff(moment(startTime), 'minutes');
  if (minutes <= 0) {
    return null;
  }
  return {minutes, units: unitsLabel(billableUnits(minutes))};
};

const isNotReached = (note: ContactNote) => note.contactMethod === NOT_REACHED;

const otherReasonOf = ({
  notReachedReason,
  notReachedReasonOther,
}: ContactNote) =>
  notReachedReason === OTHER ? notReachedReasonOther.trim() : '';

const notReachedReasonOf = (note: ContactNote) =>
  otherReasonOf(note) || labelOf(NOT_REACHED_REASONS, note.notReachedReason);

const placeOfServiceOf = ({
  placeOfServiceCode,
  placeOfServiceLabel,
  placeOfServiceOther,
}: ContactNote) => {
  const place = [placeOfServiceCode, placeOfServiceLabel]
    .filter(Boolean)
    .join(' · ');
  return placeOfServiceOther.trim()
    ? `${place} - ${placeOfServiceOther.trim()}`
    : place;
};

export const contactNoteFacts = (note: ContactNote): SummaryFact[] => {
  const dateOfService = {
    label: 'Date of service',
    value: moment(note.dateOfService).format(DATE_FORMAT_LONG),
  };

  if (isNotReached(note)) {
    return [
      dateOfService,
      {label: 'Contact', value: 'Not reached'},
      {
        label: 'Next attempt',
        value: formatDate(note.nextAttemptAt, DATE_TIME_FORMAT_LONG) || '-',
      },
      {label: 'Reason', value: notReachedReasonOf(note) || '-'},
    ];
  }

  const duration = durationOf(note);
  return [
    dateOfService,
    {label: 'Contact', value: labelOf(CONTACT_METHODS, note.contactMethod)},
    {label: 'Service code', value: note.serviceCode ?? '-'},
    {
      label: 'Duration',
      value: duration ? `${duration.minutes} min · ${duration.units}` : '-',
    },
  ];
};

export const ContactNoteFacts = ({note}: {note: ContactNote}) => (
  <NoteSummary.Facts facts={contactNoteFacts(note)} />
);

// Keeps section titles matched to FIELD_PLACEMENT, which the tests rely on.
const Section = (props: {title: SectionTitle; fields: SummaryField[]}) => (
  <NoteSummary.Section {...props} />
);

const ContactSection = ({note}: {note: ContactNote}) => {
  const contact = [
    {
      label: 'How did you contact the member?',
      value: labelOf(CONTACT_METHODS, note.contactMethod),
    },
    {label: 'Contact method details', value: note.contactMethodDetail},
  ];
  const attempt = [
    {
      label: 'Reason for the attempt',
      value: otherReasonOf(note)
        ? `Other: ${otherReasonOf(note)}`
        : notReachedReasonOf(note),
    },
    {
      label: 'Next attempt',
      value: formatDate(note.nextAttemptAt, DATE_TIME_FORMAT_WEEKDAY),
    },
    {label: 'Attempt note', value: note.notReachedNote},
  ];

  return (
    <Section
      title="Contact"
      fields={isNotReached(note) ? [...contact, ...attempt] : contact}
    />
  );
};

const ReachedSections = ({note}: {note: ContactNote}) => {
  const duration = durationOf(note);

  return (
    <>
      <Section
        title="Service provided"
        fields={[
          {label: 'Place of service', value: placeOfServiceOf(note)},
          {
            label: 'Case management service provided',
            value: tickedItems(note.activities, SERVICE_ACTIVITIES),
          },
          {label: 'Specific member need addressed', value: note.specificNeed},
          {
            label: 'Case-management intervention',
            value: note.serviceDescription,
          },
        ]}
      />
      <Section
        title="Reason"
        fields={[
          {
            label: 'Why did you contact the member?',
            value: tickedItems(note.reasons, REASONS),
          },
          {label: 'Objective reason for contact', value: note.objectiveReason},
        ]}
      />
      <Section
        title="Member status"
        fields={[
          {
            label: 'Change since last note',
            value: labelOf(CHANGE_STATUSES, note.changeStatus),
          },
          {
            label: 'Identified need',
            value: tickedItems(note.needs, STATUS_NEEDS),
          },
          {label: 'Relevant change', value: note.relevantChange},
          {
            label: 'Objective observation / member report',
            value: note.observation,
          },
        ]}
      />
      <Section
        title="Member response"
        fields={[
          {
            label: 'How did the member respond?',
            value: tickedItems(note.responses, RESPONSES),
          },
          {label: 'Member response / outcome', value: note.responseOutcome},
        ]}
      />
      <Section
        title="Follow-up"
        fields={[
          {
            label: 'What happens next?',
            value: tickedItems(note.followUps, FOLLOW_UPS),
          },
          {label: 'Follow-up plan', value: note.followUpPlan},
          {
            label: 'Follow-up date',
            value: formatDate(note.followUpAt, DATE_TIME_FORMAT_WEEKDAY),
          },
        ]}
      />
      <Section
        title="Billing"
        fields={[
          {
            label: 'Time',
            value:
              note.startTime && note.endTime
                ? `${formatDate(note.startTime, TIME_FORMAT_12)} – ${formatDate(note.endTime, TIME_FORMAT_12)}`
                : '',
          },
          {
            label: 'Billable units (15 min)',
            value: duration
              ? `${duration.minutes} minutes · ${duration.units}`
              : '',
          },
          {
            label: 'Service code',
            value: labelOf(SERVICE_CODES, note.serviceCode),
          },
        ]}
      />
    </>
  );
};

type ContactNoteSummaryProps = {
  note: ContactNote;
  narrativeAction?: ReactNode;
};

export const ContactNoteSummary = ({
  note,
  narrativeAction,
}: ContactNoteSummaryProps) => (
  <NoteSummary>
    <NoteSummary.Narrative text={note.narrative} action={narrativeAction} />
    <ContactSection note={note} />
    {!isNotReached(note) && <ReachedSections note={note} />}
  </NoteSummary>
);
